import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import ButtonComponent from "../../../Components/Common/button/Button";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../Components/Common/datatable/DataTable";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import ApiService from "../../../utils/fetch";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FGPlantSelectionTree from "./FGPlantSelectionTree";
import { useSelector } from "react-redux";
import FGManualUpdate from "./FGManualUpdate";
import { safeTimestamp } from "../../../Components/Common/DateFormatter";

function FGMaterialConfig() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [rowNumber, setRowNumber] = useState(1);
  const [samplePositionData, setSamplePositionData] = useState([]);

  const [activeTemplateTableTab, setActiveTemplateTableTab] = useState(0);
  const [templateDatas, setTemplateDatas] = useState([]);
  const [templateHistoryDatas, setTemplateHistoryDatas] = useState([]);
  const [selectedTemplateRows, setSelectedTemplateRows] = useState([]);

  const positionRef = useRef(null);
  const positionTableRef = useRef(null);
  const activeTemplateRef = useRef(null);
  const historyTemplateRef = useRef(null);

  const [bottlerOwnerCodePosition, setBottlerOwnerCodePosition] =
    useState(null);
  const [countryPosition, setCountryPosition] = useState(null);
  const [skuPosition, setskuPosition] = useState(null);
  const [productType, setProductType] = useState(null);
  const [months, setMonths] = useState(null);
  const [quantityPosition, setQuantityPosition] = useState(null);
  const [inventoryDatePosition, setInventoryDatePosition] = useState(null);

  const [appliedTreeData, setAppliedTreeData] = useState([]);

  const AppliedSelectedData =
    useSelector((state) => state?.FGTemplateTree?.FGTemplateAppliedData) || [];

  const FormatType = ["Customer SKU"];
  const noOfMonths = [
    "12 Months",
    "13 Months",
    "14 Months",
    "15 Months",
    "16 Months",
    "17 Months",
    "18 Months",
  ];

  // common Pagination States
  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];
  const topSectionHeightPx = 500;
  const isFileUploaded =
    Array.isArray(samplePositionData) && samplePositionData.length > 0;

  // Column definitions based on samplePositionData
  const PositionDefinition = useMemo(() => {
    if (!Array.isArray(samplePositionData) || samplePositionData.length === 0) {
      return [];
    }

    return samplePositionData.map((item, index) => ({
      field: `col${index}`,
      headerName: item.fieldname || `Column ${index + 1}`,
      minWidth: 150,
      resizable: true,
      flex: 1,
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: true,
      cellRenderer: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <div>{params.value || ""}</div>
        </Tooltip>
      ),
    }));
  }, [samplePositionData]);

  // Create table rows with fieldPosition and fieldname
  const tableRows = useMemo(() => {
    if (!Array.isArray(samplePositionData) || samplePositionData.length === 0) {
      return [];
    }

    const row = samplePositionData.reduce(
      (rowObj, item, index) => {
        rowObj[`col${index}`] = `${item.fieldPosition}. ${item.fieldname}`;
        return rowObj;
      },
      { id: "row-0" },
    );

    return [row];
  }, [samplePositionData]);

  const getPositionKey = (option) =>
    option ? `${option.fieldPosition}::${option.fieldname}` : "";

  const selectedTargetKeys = useMemo(() => {
    return new Set(
      [countryPosition, skuPosition, quantityPosition, inventoryDatePosition]
        .filter(Boolean)
        .map((item) => getPositionKey(item)),
    );
  }, [countryPosition, skuPosition, quantityPosition, inventoryDatePosition]);

  const getAvailableTargetOptions = (currentSelection) => {
    const currentKey = getPositionKey(currentSelection);

    return samplePositionData.filter((option) => {
      const optionKey = getPositionKey(option);
      return !selectedTargetKeys.has(optionKey) || optionKey === currentKey;
    });
  };

  const onPositionGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  };

  const CustomNoRowsOverlay = ({ noRowsMessage }) => {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          fontSize: "18px",
          color: "#000",
        }}
      >
        {noRowsMessage}
      </div>
    );
  };

  function dateComparator(date1, date2) {
    // Convert both ISO 8601 strings to timestamps (milliseconds since Unix Epoch)
    const date1Number = _isoDateToTimestamp(date1);
    const date2Number = _isoDateToTimestamp(date2);

    // Handle null or invalid dates
    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    // Compare timestamps
    return date1Number - date2Number;
  }

  function _isoDateToTimestamp(isoDate) {
    if (!isoDate || typeof isoDate !== "string") {
      return null;
    }

    const date = new Date(isoDate);
    // Check if Date parsing failed
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  }

  //template configuration upload API

  const templateConfigUpload = async (event) => {
    if (isUploading) return;

    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select a file!",
      });
      return;
    }
    setUploadLoading(true);

    const uploadRequestDTO = {
      rowNumber: rowNumber,
    };

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("uploadRequestDTOJson", JSON.stringify(uploadRequestDTO));

    setIsUploading(true);
    try {
      const response = await ApiService.upload(
        "/v1/sample-template-upload",
        formData,
      );
      setSamplePositionData(response.data);
    } catch (error) {
      console.error("Error uploading template configuration file:", error);
      MessagePopup({
        icon: "error",
        title: "Error!",
        message:
          "Failed to upload template configuration file. Please ensure the file contains valid data.",
      });
    } finally {
      // Reset loading state
      setUploadLoading(false);
      setIsUploading(false);
    }
  };

  const templateDefinition = [
    {
      headerName: "",
      checkboxSelection: true,
      width: 40,
      minWidth: 40,
      pinned: "left",
      suppressMenu: true,
      suppressMovable: true,
      headerCheckboxSelection: true,
    },
    {
      field: "bottlerOwnerCode",
      headerName: "Bottler Owner Code",
      sortable: true,
      unSortIcon: true,
      width: 230,
      minWidth: 230,
      valueGetter: (params) => {
        const { bottlerOwnerCode, bottlerOwnerDescription } = params.data;
        return `${bottlerOwnerCode || ""}-${bottlerOwnerDescription || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "customerCountry",
      headerName: "Country",
      sortable: true,
      unSortIcon: true,
      width: 190,
      minWidth: 190,
      valueGetter: (params) => {
        const { customerCountry } = params.data;
        return `${customerCountry || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "countryPosition",
      headerName: "Country Position",
      sortable: true,
      unSortIcon: true,
      width: 210,
      minWidth: 210,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "skuPosition",
      headerName: "Customer SKU Position",
      sortable: true,
      unSortIcon: true,
      width: 260,
      minWidth: 260,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "inventoryDatePosition",
      headerName: "Inventory-Date Position",
      sortable: true,
      unSortIcon: true,
      width: 250,
      minWidth: 250,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "quantityPosition",
      headerName: "FG Inventory Quantity Position",
      sortable: true,
      unSortIcon: true,
      width: 300,
      minWidth: 300,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "createdBy",
      headerName: "Created By",
      sortable: true,
      unSortIcon: true,
      width: 220,
      minWidth: 220,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date(UTC)",
      sortable: true,
      unSortIcon: true,
      width: 250,
      minWidth: 250,
      filterParams: {
        comparator: function (filterDate, cellValue) {
          if (!cellValue) return -1;

          const cellDate = new Date(cellValue);
          // Strip time and compare only YYYY-MM-DD
          const cellDateOnly = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          return cellDateOnly.getTime() - filterDate.getTime();
        },
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
        closeOnApply: true,
        onApply: (params) => params.api.hidePopupMenu(),
        onReset: (params) => params.api.hidePopupMenu(),
      },
      filter: "agDateColumnFilter",
      comparator: dateComparator,
      valueFormatter: (params) => {
        return safeTimestamp(params.value);
      },
    },
    // {
    //   field: "forecastMonthCount",
    //   headerName: "No of Months",
    //   sortable: true,
    //   unSortIcon: true,
    //   width: 190,
    //   minWidth: 190,
    //   filter: "agMultiColumnFilter",
    //   filterParams: {
    //     filters: [
    //       { filter: "agTextColumnFilter" },
    //       { filter: "agSetColumnFilter" },
    //     ],
    //   },
    // },
  ];

  const templateHistoryDefinition = [
    // Map active/template columns but convert backend's `createdAt` -> history API `createdDate`
    ...templateDefinition
      .filter((col) => col.field)
      .map((col) => {
        if (col.field === "createdAt") {
          return {
            ...col,
            field: "createdDate",
            headerName: "Created Date(UTC)",
            filterParams: {
              comparator: function (filterDate, cellValue) {
                if (!cellValue) return -1;

                const cellDate = new Date(cellValue);
                const cellDateOnly = new Date(
                  cellDate.getFullYear(),
                  cellDate.getMonth(),
                  cellDate.getDate(),
                );

                return cellDateOnly.getTime() - filterDate.getTime();
              },
              filterOptions: ["equals", "inRange"],
              defaultOption: "equals",
              buttons: ["apply", "reset"],
              closeOnApply: true,
              onApply: (params) => params.api.hidePopupMenu(),
              onReset: (params) => params.api.hidePopupMenu(),
            },
            filter: "agDateColumnFilter",
            comparator: dateComparator,
            valueFormatter: (params) => {
              return safeTimestamp(params.value);
            },
          };
        }

        return col;
      }),
    {
      field: "isActive",
      headerName: "Is Active",
      sortable: true,
      unSortIcon: true,
      width: 150,
      minWidth: 150,
      cellRenderer: (params) => {
        const isActiveValue =
          params.value === true ||
          String(params.value).toLowerCase() === "true";

        return (
          <Chip
            label={isActiveValue ? "Active" : "Inactive"}
            size="small"
            sx={{
              backgroundColor: isActiveValue ? "#6ACE7F" : "#f40000",
              color: "#fff",
              fontWeight: 600,
            }}
          />
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
    },
  ];

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  const onTemplateSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedTemplateRows(selected);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTemplateTableTabChange = (event, newValue) => {
    setActiveTemplateTableTab(newValue);
  };

  useEffect(() => {
    if (
      activeTemplateTableTab === 0 &&
      activeTemplateRef.current &&
      activeTemplateRef.current.api &&
      templateDatas.length > 0
    ) {
      activeTemplateRef.current.api.sizeColumnsToFit();
    }

    if (
      activeTemplateTableTab === 1 &&
      historyTemplateRef.current &&
      historyTemplateRef.current.api &&
      templateHistoryDatas.length > 0
    ) {
      historyTemplateRef.current.api.sizeColumnsToFit();
    }
  }, [templateDatas, templateHistoryDatas, activeTemplateTableTab]);

  const templateConfigUploadSave = async () => {
    if (
      !countryPosition ||
      !skuPosition ||
      !quantityPosition ||
      // !productType ||
      // !months ||
      // !bottlerOwnerCodePosition ||
      !inventoryDatePosition
    ) {
      MessagePopup({
        icon: "error",
        title: "Missing Fields",
        message: "Please select all required fields.",
      });
      return;
    }
    if (appliedTreeData.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Please select at least one country from Selection.",
      });
      return;
    }

    const positions = [
      countryPosition.fieldPosition,
      skuPosition.fieldPosition,
      quantityPosition.fieldPosition,
      // bottlerOwnerCodePosition.fieldPosition,
      inventoryDatePosition.fieldPosition,
    ];

    const uniquePositions = new Set(positions);

    if (uniquePositions.size < positions.length) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message:
          "Duplicate values detected. Please ensure all values are unique.",
      });
      return;
    }

    const result = await MessagePopup({
      title: "Confirm Save",
      html: `<p>Are you sure want to save the Configuration?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });
    if (!result.isConfirmed) return;

    const requestBody = {
      bottlerCountries: appliedTreeData,
      // conversionType: productType,
      countryPosition: countryPosition.fieldPosition,
      skuPosition: skuPosition.fieldPosition,
      quantityPosition: quantityPosition.fieldPosition,
      // forecastMonthCount: months,
      // bottlerOwnerCodePosition: bottlerOwnerCodePosition.fieldPosition,
      inventoryDatePosition: inventoryDatePosition.fieldPosition,
    };

    try {
      const response = await ApiService.post(
        `/v1/custom-fg-inventory/saveConfiguration`,
        requestBody,
      );
      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Template configuration saved successfully.",
      });

      setCountryPosition(null);
      setskuPosition(null);
      setProductType(null);
      setQuantityPosition(null);
      setBottlerOwnerCodePosition(null);
      setInventoryDatePosition(null);
      // setMonths(null);

      templateTableData(appliedTreeData);
      templateHistoryTableData();
    } catch (error) {
      console.error("Error saving template configuration:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to Save. Please try again.",
      });
    }
  };

  //template configuration get API for Table
  const handleApplySelection = (AppliedSelectedData) => {
    templateTableData(AppliedSelectedData);
    setAppliedTreeData(AppliedSelectedData);
  };

  const templateTableData = async (AppliedSelectedData) => {
    if (!AppliedSelectedData || AppliedSelectedData.length === 0) {
      setTemplateDatas([]);
      return;
    }
    try {
      const response = await ApiService.post(
        `/v1/custom-fg-inventory/fetchConfiguration`,
        AppliedSelectedData,
      );
      setTemplateDatas(response.data);
    } catch (error) {
      console.error("Error fetching template data:", error);
    }
  };

  const templateHistoryTableData = async () => {
    try {
      const response = await ApiService.get(
        `/v1/custom-fg-inventory/fetchConfigurationHistory`,
      );
      setTemplateHistoryDatas(response.data);
    } catch (error) {
      console.error("Error fetching template history data:", error);
    }
  };

  useEffect(() => {
    templateHistoryTableData();
  }, []);

  const templateConfigTableDelete = async () => {
    if (selectedTemplateRows.length === 0) {
      MessagePopup({
        icon: "error",
        title: "No Selection",
        message: "Please select at least one row to delete.",
      });
      return;
    }
    const result = await MessagePopup({
      title: "Confirm Delete",
      html: `<p>Are you sure want to Delete the Configuration?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });
    if (!result.isConfirmed) return;

    const bottlerCountries = selectedTemplateRows.map((row) => ({
      customerCountry: row.customerCountry,
      bottlerOwnerCode: row.bottlerOwnerCode,
      bottler: row.bottler,
    }));

    const requestBody = {
      bottlerCountries: bottlerCountries,
    };
    try {
      const response = await ApiService.post(
        `/v1/custom-fg-inventory/deleteConfiguration`,
        requestBody,
      );
      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Template configuration Deleted successfully.",
      });

      if (activeTemplateRef.current && activeTemplateRef.current.api) {
        activeTemplateRef.current.api.refreshCells({ force: true });
      }
      templateTableData(AppliedSelectedData);
      templateHistoryTableData();
      setSelectedTemplateRows([]);
    } catch (error) {
      console.error("Error Deleting template configuration:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to Delete. Please try again.",
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                fontSize: "14px",
                textTransform: "none",
                minWidth: "120px",
              },
            }}
          >
            <Tab label="Template Configuration" />
            {/* <Tab label="Active BPP" /> */}
          </Tabs>
        </Box>
        <ButtonComponent
          label="Back"
          bgColor="black"
          color="white"
          onClick={() =>
            navigate("/fg-manage-forecast", { state: { activeTab: 1 } })
          }
          sx={{ ml: 2 }}
        />
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 0}>
        <Grid container spacing={1}>
          <Grid item xs={8}>
            {/* First Row */}
            <div
              className="container"
              style={{ height: `${topSectionHeightPx}px`, overflowY: "auto" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h10"
                  sx={{
                    fontWeight: 450,
                  }}
                >
                  Set Column Positions by Upload
                </Typography>
                <ButtonComponent
                  label={isUploading ? "Uploading..." : "File Upload"}
                  bgColor="black"
                  color="white"
                  onClick={() => {
                    if (positionRef.current) {
                      positionRef.current.value = "";
                      positionRef.current.click();
                    }
                  }}
                  disabled={isUploading}
                  startIcon={<FileUploadIcon />}
                />
                <input
                  type="file"
                  ref={positionRef}
                  style={{ display: "none" }}
                  accept=".xlsx,.xls"
                  onChange={templateConfigUpload}
                />
                <Grid item xs={3} sx={{ mt: 0.25 }}>
                  <TextField
                    id="rowReader"
                    label="Excel Row Reader"
                    type="number"
                    value={rowNumber}
                    onChange={(e) => setRowNumber(e.target.value)}
                    InputProps={{
                      style: { fontSize: "13px" },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        transform: "translate(14px, -14px) scale(0.75)",
                      },
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Box>
              <DataTable
                ref={positionTableRef}
                columns={PositionDefinition}
                rows={tableRows || []}
                tableHeight={"15vh"}
                suppressMovableColumns={true}
                suppressHorizontalScroll={false}
                suppressPaginationPanel={true}
                pagination={pagination}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                onGridReady={onPositionGrid}
                headerHeight={0}
                noRowsOverlayComponent={CustomNoRowsOverlay}
                noRowsOverlayComponentParams={{
                  noRowsMessage: "",
                }}
              />
              <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                <Grid item xs={12} container columnSpacing={1} rowSpacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Source Field</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Target Field</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography sx={{ mt: 1, fontSize: "14px" }}>
                      Customer Country
                      <Box component="span" sx={{ color: "red" }}>
                        *
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={getAvailableTargetOptions(countryPosition)}
                      disabled={!isFileUploaded}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              countryPosition?.fieldPosition &&
                            option.fieldname === countryPosition?.fieldname,
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.fieldPosition === value.fieldPosition &&
                        option.fieldname === value.fieldname
                      }
                      onChange={(event, newValue) => {
                        setCountryPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null,
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography sx={{ mt: 1, fontSize: "14px" }}>
                      Customer SKU
                      <Box component="span" sx={{ color: "red" }}>
                        *
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={getAvailableTargetOptions(skuPosition)}
                      disabled={!isFileUploaded}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              skuPosition?.fieldPosition &&
                            option.fieldname === skuPosition?.fieldname,
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.fieldPosition === value.fieldPosition &&
                        option.fieldname === value.fieldname
                      }
                      onChange={(event, newValue) => {
                        setskuPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null,
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography sx={{ mt: 1, fontSize: "14px" }}>
                      FG Inventory Quantity Start
                      <Box component="span" sx={{ color: "red" }}>
                        *
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={getAvailableTargetOptions(quantityPosition)}
                      disabled={!isFileUploaded}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              quantityPosition?.fieldPosition &&
                            option.fieldname === quantityPosition?.fieldname,
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.fieldPosition === value.fieldPosition &&
                        option.fieldname === value.fieldname
                      }
                      onChange={(event, newValue) => {
                        setQuantityPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null,
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography sx={{ mt: 1, fontSize: "14px" }}>
                      Inventory Date
                      <Box component="span" sx={{ color: "red" }}>
                        *
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={getAvailableTargetOptions(inventoryDatePosition)}
                      disabled={!isFileUploaded}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              inventoryDatePosition?.fieldPosition &&
                            option.fieldname ===
                              inventoryDatePosition?.fieldname,
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.fieldPosition === value.fieldPosition &&
                        option.fieldname === value.fieldname
                      }
                      onChange={(event, newValue) => {
                        setInventoryDatePosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null,
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography sx={{ mt: 1, fontSize: "14px" }}>
                      No of Months
                      <Box component="span" sx={{ color: "red" }}>
                        *
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      value="1 Month"
                      size="small"
                      fullWidth
                      disabled
                      InputLabelProps={{
                        shrink: true,
                        style: {
                          transform: "translate(14px, -14px) scale(0.75)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <ButtonComponent
                      bgColor="black"
                      color="white"
                      label="Save"
                      onClick={templateConfigUploadSave}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </Grid>
          <Grid item xs={4} spacing={1}>
            <FGPlantSelectionTree
              title="Selection"
              onApplySelection={handleApplySelection}
              containerHeight={topSectionHeightPx}
            />
          </Grid>
        </Grid>
        <div className="container">
          <Grid
            container
            spacing={1}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Grid item xs={9}>
              <Tabs
                value={activeTemplateTableTab}
                onChange={handleTemplateTableTabChange}
                sx={{
                  "& .MuiTab-root": {
                    fontSize: "13px",
                    textTransform: "none",
                    minWidth: "220px",
                    p: 1,
                  },
                }}
              >
                <Tab label="Active Configuration" />
                <Tab label="Configuration History" />
              </Tabs>
            </Grid>
            <Grid
              item
              xs={3}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              {activeTemplateTableTab === 0 && (
                <ButtonComponent
                  label="Delete"
                  bgColor="black"
                  color="white"
                  onClick={templateConfigTableDelete}
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {activeTemplateTableTab === 0 ? (
                <DataTable
                  ref={activeTemplateRef}
                  columns={templateDefinition}
                  rows={templateDatas || []}
                  tableHeight={"50vh"}
                  suppressMovableColumns={true}
                  suppressHorizontalScroll={false}
                  suppressPaginationPanel={false}
                  pagination={pagination}
                  paginationPageSize={paginationPageSize}
                  paginationPageSizeSelector={paginationPageSizeSelector}
                  onGridReady={onGrid}
                  rowSelection="multiple"
                  onSelectionChanged={onTemplateSelectionChanged}
                />
              ) : (
                <DataTable
                  ref={historyTemplateRef}
                  columns={templateHistoryDefinition}
                  rows={templateHistoryDatas || []}
                  tableHeight={"50vh"}
                  suppressMovableColumns={true}
                  suppressHorizontalScroll={false}
                  suppressPaginationPanel={false}
                  pagination={pagination}
                  paginationPageSize={paginationPageSize}
                  paginationPageSizeSelector={paginationPageSizeSelector}
                  onGridReady={onGrid}
                  rowSelection="single"
                />
              )}
            </Grid>
          </Grid>
        </div>
      </Box>
      {/* <Box role="tabpanel" hidden={activeTab !== 1}>
        <FGManualUpdate activeTab={activeTab} />
      </Box> */}
    </>
  );
}
export default FGMaterialConfig;
